import React from 'react';
import { Link, useNavigate } from 'react-router-dom';

const s = {
  page: { maxWidth: 800, margin: '0 auto', padding: '40px 20px', color: 'var(--text)' },
  back: { color: 'var(--primary)', cursor: 'pointer', fontWeight: 600, fontSize: 15, marginBottom: 32, display: 'inline-block', textDecoration: 'none' },
  hero: { background: 'var(--primary)', borderRadius: 16, padding: '36px 40px', marginBottom: 40, color: '#fff' },
  heroTitle: { fontSize: 32, fontWeight: 800, marginBottom: 8 },
  heroSub: { fontSize: 15, opacity: 0.85 },
  section: { background: 'var(--surface)', borderRadius: 14, padding: '28px 32px', marginBottom: 20, boxShadow: 'var(--shadow)' },
  h2: { fontSize: 20, fontWeight: 700, color: 'var(--heading)', marginBottom: 14, marginTop: 0, display: 'flex', alignItems: 'center', gap: 10 },
  p: { color: 'var(--text2)', fontSize: 15, lineHeight: 1.7, marginBottom: 12, marginTop: 0 },
  ul: { color: 'var(--text2)', fontSize: 15, lineHeight: 1.8, paddingLeft: 22, marginBottom: 12, marginTop: 0 },
  highlight: { background: 'var(--warning-bg)', color: 'var(--warning-text)', borderRadius: 10, padding: '14px 18px', fontSize: 14, fontWeight: 600, marginBottom: 20, borderLeft: '4px solid #f59e0b' },
  footer: { textAlign: 'center', color: 'var(--text4)', fontSize: 14, marginTop: 40, paddingTop: 24, borderTop: '1px solid var(--border)' },
  footerLink: { color: 'var(--primary)', textDecoration: 'none', fontWeight: 600 },
};

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  const lastUpdated = 'March 15, 2026';

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <div style={s.page}>

        <span style={s.back} onClick={() => navigate(-1)}>← Go Back</span>

        <div style={s.hero}>
          <div style={s.heroTitle}>🔒 Privacy Policy</div>
          <div style={s.heroSub}>Last updated: {lastUpdated} · Effective immediately</div>
        </div>

        <div style={s.highlight}>
          ⚕️ <strong>Health Data Notice:</strong> SeniorCare handles sensitive personal and medical information. We are committed to keeping your data private, secure, and used only to provide the service you signed up for.
        </div>

        {/* 1 */}
        <div style={s.section}>
          <h2 style={s.h2}>📋 1. Who We Are</h2>
          <p style={s.p}>
            SeniorCare ("we", "us", or "our") is a care management application designed to help families and caregivers track medications, appointments, vital signs, and daily care journals for elderly individuals.
          </p>
          <p style={s.p}>
            By using SeniorCare, you agree to the collection and use of information in accordance with this Privacy Policy.
          </p>
        </div>

        {/* 2 */}
        <div style={s.section}>
          <h2 style={s.h2}>📦 2. Information We Collect</h2>
          <p style={s.p}>We collect the following types of information:</p>
          <p style={s.p}><strong>Account Information:</strong></p>
          <ul style={s.ul}>
            <li>Full name, email address, date of birth</li>
            <li>Password (stored as an encrypted hash — never in plain text)</li>
            <li>Profile photo (optional)</li>
          </ul>
          <p style={s.p}><strong>Care Data (entered by you):</strong></p>
          <ul style={s.ul}>
            <li>Senior profiles: name, date of birth, phone number, notes</li>
            <li>Caregiver profiles: name, relationship, phone number</li>
            <li>Medications: name, dosage, reminder times, instructions</li>
            <li>Vital signs: blood pressure, heart rate, weight, blood glucose readings</li>
            <li>Daily journal entries: mood, activity, appetite, observations</li>
            <li>Appointments: title, type, date, time, location, results/notes</li>
          </ul>
          <p style={s.p}><strong>Technical Information:</strong></p>
          <ul style={s.ul}>
            <li>IP address and browser type (for security and rate limiting)</li>
            <li>Login timestamps</li>
          </ul>
        </div>

        {/* 3 */}
        <div style={s.section}>
          <h2 style={s.h2}>🎯 3. How We Use Your Information</h2>
          <p style={s.p}>We use your information solely to:</p>
          <ul style={s.ul}>
            <li>Provide and operate the SeniorCare service</li>
            <li>Send medication reminder SMS messages (via Twilio) to phone numbers you provide</li>
            <li>Send appointment reminder notifications</li>
            <li>Send SOS emergency alerts to designated caregivers</li>
            <li>Generate PDF health reports that you request</li>
            <li>Send email reports to addresses you specify</li>
            <li>Send account verification and password reset emails</li>
            <li>Improve app performance and fix bugs</li>
          </ul>
          <p style={s.p}><strong>We never sell, rent, or share your data with third parties for marketing purposes.</strong></p>
        </div>

        {/* 4 */}
        <div style={s.section}>
          <h2 style={s.h2}>📱 4. SMS & Email Communications</h2>
          <p style={s.p}>
            By adding a phone number to a senior or caregiver profile, you consent to receive SMS medication reminders and emergency alerts at that number. Standard message rates may apply depending on your carrier.
          </p>
          <p style={s.p}>
            You can stop SMS reminders at any time by removing the phone number from the profile. Transactional emails (password reset, account verification) are sent only when you initiate the action.
          </p>
        </div>

        {/* 5 */}
        <div style={s.section}>
          <h2 style={s.h2}>🔐 5. Data Security</h2>
          <p style={s.p}>We take the following measures to protect your data:</p>
          <ul style={s.ul}>
            <li>Passwords are hashed using <strong>bcrypt</strong> — never stored in plain text</li>
            <li>All API communication is authenticated using <strong>JWT tokens</strong></li>
            <li>Rate limiting is applied to login, registration, and SOS endpoints to prevent abuse</li>
            <li>Security headers are set using <strong>Helmet.js</strong></li>
            <li>File uploads are validated for type and size</li>
          </ul>
          <p style={s.p}>
            While we take reasonable precautions, no method of internet transmission is 100% secure. We cannot guarantee absolute security of your data.
          </p>
        </div>

        {/* 6 */}
        <div style={s.section}>
          <h2 style={s.h2}>🍪 6. Cookies & Local Storage</h2>
          <p style={s.p}>
            SeniorCare uses <strong>browser localStorage</strong> to store your login session token, user preferences (language, dark/light mode), and cached user profile. No third-party tracking cookies are used.
          </p>
          <p style={s.p}>
            Clearing your browser's localStorage will log you out and reset your preferences.
          </p>
        </div>

        {/* 7 */}
        <div style={s.section}>
          <h2 style={s.h2}>👶 7. Children's Privacy</h2>
          <p style={s.p}>
            SeniorCare is intended for users 18 years of age and older. We do not knowingly collect personal information from individuals under 18. If you believe a minor has provided us with personal data, please contact us immediately.
          </p>
        </div>

        {/* 8 */}
        <div style={s.section}>
          <h2 style={s.h2}>✏️ 8. Your Rights</h2>
          <p style={s.p}>You have the right to:</p>
          <ul style={s.ul}>
            <li><strong>Access</strong> — view all data stored in your account at any time through the app</li>
            <li><strong>Correct</strong> — update your profile, senior/caregiver data, and records at any time</li>
            <li><strong>Delete</strong> — delete any senior, caregiver, medication, or journal entry; deleting a senior removes all associated data</li>
            <li><strong>Export</strong> — generate PDF reports of health data for any senior or caregiver</li>
            <li><strong>Account deletion</strong> — contact us to permanently delete your entire account and all associated data</li>
          </ul>
        </div>

        {/* 9 */}
        <div style={s.section}>
          <h2 style={s.h2}>🔗 9. Third-Party Services</h2>
          <p style={s.p}>SeniorCare uses the following third-party services, each governed by their own privacy policies:</p>
          <ul style={s.ul}>
            <li><strong>Twilio</strong> — SMS delivery for medication and appointment reminders</li>
            <li><strong>Gmail / Nodemailer</strong> — transactional email delivery</li>
            <li><strong>Stripe</strong> — payment processing for subscriptions (card data never touches our servers)</li>
          </ul>
        </div>

        {/* 10 */}
        <div style={s.section}>
          <h2 style={s.h2}>🔄 10. Changes to This Policy</h2>
          <p style={s.p}>
            We may update this Privacy Policy from time to time. We will notify you of significant changes by posting a notice in the app or sending an email. Continued use of the app after changes constitutes acceptance of the updated policy.
          </p>
        </div>

        {/* 11 */}
        <div style={s.section}>
          <h2 style={s.h2}>📧 11. Contact Us</h2>
          <p style={s.p}>
            If you have any questions, concerns, or requests regarding this Privacy Policy or your personal data, please contact us at:
          </p>
          <ul style={s.ul}>
            <li><strong>App:</strong> SeniorCare</li>
            <li><strong>Email:</strong> privacy@seniorcare.app</li>
          </ul>
        </div>

        <div style={s.footer}>
          <p style={{ margin: '0 0 10px' }}>
            <Link to="/terms" style={s.footerLink}>Terms of Service</Link>
            {' · '}
            <Link to="/privacy" style={s.footerLink}>Privacy Policy</Link>
            {' · '}
            <Link to="/login" style={s.footerLink}>Sign In</Link>
          </p>
          <p style={{ margin: 0 }}>© {new Date().getFullYear()} SeniorCare. All rights reserved.</p>
        </div>

      </div>
    </div>
  );
}
